import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";

const ITEMS_PER_PAGE = 8;
const DIFICULDADES = ["ALTA", "MÉDIA", "BAIXA"];

export default function ConteudosPage() {
  const { dark } = useTheme();

  const [conteudos, setConteudos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [view, setView] = useState("list");
  const [editingConteudo, setEditingConteudo] = useState(null);
  const [deletingConteudo, setDeletingConteudo] = useState(null);
  const [notification, setNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [gerandoFormacaoId, setGerandoFormacaoId] = useState(null);

  // ══════════ Controlo do limite de formações ══════════
  const [formStats, setFormStats] = useState({ total_permitido: 0, total_criado: 0, total_restante: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  const loadFormStats = useCallback(async () => {
    try {
      const res = await Api.getTotalFormacoes();
      const data = res?.data || res;
      if (data && typeof data.total_permitido === 'number') {
        setFormStats({
          total_permitido: data.total_permitido || 0,
          total_criado: data.total_criado || 0,
          total_restante: data.total_restante || 0,
        });
      }
    } catch {
      // se falhar, mantém o valor anterior
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // ══════════ Tokens de cor ══════════
  const t = dark
    ? {
        bg: "#080c14", bgCard: "#111827", bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)", bgInput: "#1a2232",
        bgDropdown: "#111827", border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)", textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0", textMuted: "#7b93b0",
        textActive: "#00b4ff", shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)", danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)", success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)", warning: "#f59e0b",
        warningBg: "rgba(245,158,11,0.12)", overlay: "rgba(8,12,20,0.8)",
      }
    : {
        bg: "#f4f7fc", bgCard: "#ffffff", bgHover: "#f0f6ff",
        bgActive: "#e6f0ff", bgInput: "#fafbfd", bgDropdown: "#ffffff",
        border: "#e2e8f0", borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e", textSecondary: "#475569",
        textMuted: "#8b9eb0", textActive: "#0057ff",
        shadow: "0 8px 30px rgba(0,50,150,0.06)", rowHover: "#f8faff",
        danger: "#ef4444", dangerBg: "#fef2f2", success: "#16a34a",
        successBg: "#f0fdf4", warning: "#d97706", warningBg: "#fffbeb",
        overlay: "rgba(15,23,42,0.4)",
      };

  // ── Carregar dados ─────────────────────────────
  const loadConteudos = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await Api.getConteudo();
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setConteudos(list);
    } catch {
      setErrorMsg("Não foi possível carregar os conteúdos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConteudos();
    loadFormStats();
  }, [loadConteudos, loadFormStats]);

  useEffect(() => {
    if (notification) {
      const id = setTimeout(() => setNotification(null), 3500);
      return () => clearTimeout(id);
    }
  }, [notification]);

  useEffect(() => setPage(1), [search]);

  // ── Filtro e paginação ──────────────────────────
  const filtered = useMemo(() => {
    if (!search) return conteudos;
    const q = search.toLowerCase();
    return conteudos.filter(c =>
      c.titulo?.toLowerCase().includes(q) ||
      c.texto?.toLowerCase().includes(q) ||
      c.dificuldade?.toLowerCase().includes(q)
    );
  }, [conteudos, search]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, safePage]);

  // ── Ações ──────────────────────────────────────
  const openAdd = () => { setEditingConteudo(null); setView("add"); };
  const openEdit = (c) => { setEditingConteudo(c); setView("edit"); };
  const closeForm = () => { setView("list"); setEditingConteudo(null); };

  const handleSaved = (msg) => {
    closeForm();
    setNotification({ type: "success", text: msg });
    loadConteudos();
  };

  const handleDelete = async () => {
    if (!deletingConteudo) return;
    setActionLoading(true);
    try {
      const res = await Api.deleteConteudo({ id: deletingConteudo.id });
      if (res?.success === false) {
        setNotification({ type: "error", text: res.message || "Não foi possível eliminar." });
      } else {
        setNotification({ type: "success", text: "Conteúdo eliminado." });
      }
      setDeletingConteudo(null);
      loadConteudos();
    } catch {
      setNotification({ type: "error", text: "Erro ao eliminar." });
    } finally {
      setActionLoading(false);
    }
  };

  const handleGerarFormacao = async (conteudo) => {
    if (formStats.total_restante === 0) {
      setNotification({ type: "error", text: "Limite de formações atingido." });
      return;
    }
    setGerandoFormacaoId(conteudo.id);
    try {
      const res = await Api.gerarFormacao?.({ id: conteudo.id });
      if (!res || res.success === false) {
        setNotification({ type: "error", text: res?.message || "Erro ao gerar formação." });
      } 
        else if(res.status===404){
           setNotification({ type: "error", text: "Não pode criar formações" });
        // Atualiza estatísticas de formações
        loadFormStats();
          
        }
      
      else {
        setNotification({ type: "success", text: "Formação gerada com sucesso." });
        // Atualiza estatísticas de formações
        loadFormStats();
      }
    } catch {
      setNotification({ type: "error", text: "Erro ao gerar formação." });
    } finally {
      setGerandoFormacaoId(null);
    }
  };

  // ══════════════════ Estilos CSS ═══════════════
  const styles = `
    .con-page { font-family: 'Inter', system-ui, sans-serif; background: ${t.bg}; min-height: 100vh; padding: 32px 20px; }
    .con-container { max-width: 1200px; margin: 0 auto; }
    .con-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 32px; flex-wrap: wrap; }
    .con-heading { font-size: 1.65rem; font-weight: 700; letter-spacing: -0.03em; color: ${t.textPrimary}; margin-bottom: 4px; }
    .con-sub { font-size: 0.88rem; color: ${t.textMuted}; font-weight: 400; }
    .con-header-right { display: flex; align-items: center; gap: 16px; }

    .con-btn-add { display: inline-flex; align-items: center; gap: 8px; height: 44px; padding: 0 24px; border-radius: 14px; border: none; background: linear-gradient(135deg, #00aaff, #0044ee); color: #fff; font-weight: 600; font-size: 0.86rem; cursor: pointer; box-shadow: 0 6px 18px rgba(0,100,255,0.3); transition: all 0.25s; letter-spacing: -0.01em; }
    .con-btn-add:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(0,100,255,0.45); }

    /* Widget de limite de formações */
    .con-limit-widget {
      display: flex; align-items: center; gap: 12px;
      background: ${t.bgCard}; border: 1px solid ${t.border};
      border-radius: 14px; padding: 12px 18px;
      color: ${t.textSecondary}; font-size: 0.84rem; font-weight: 500;
    }
    .con-limit-number { font-weight: 700; color: ${t.textPrimary}; }
    .con-limit-number.warning { color: ${t.danger}; }
    .con-limit-bar {
      width: 80px; height: 6px; background: ${t.bgHover};
      border-radius: 99px; overflow: hidden;
    }
    .con-limit-fill {
      height: 100%; background: ${t.success};
      border-radius: 99px; transition: width 0.3s;
    }
    .con-limit-fill.warning { background: ${t.danger}; }

    .con-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; flex-wrap: wrap; }
    .con-search-box { display: flex; align-items: center; gap: 8px; background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 14px; padding: 0 16px; height: 44px; width: 300px; transition: border 0.2s, box-shadow 0.2s; }
    .con-search-box:focus-within { border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.18); }
    .con-search-box input { background: none; border: none; outline: none; font-size: 0.86rem; color: ${t.textPrimary}; width: 100%; }
    .con-search-box input::placeholder { color: ${t.textMuted}; }

    .con-table-card { background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 18px; overflow: hidden; }
    .con-table { width: 100%; border-collapse: collapse; }
    .con-table thead th { text-align: left; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${t.textMuted}; padding: 14px 22px; background: ${t.bgHover}; border-bottom: 1px solid ${t.border}; }
    .con-table tbody td { padding: 14px 22px; font-size: 0.88rem; color: ${t.textPrimary}; border-bottom: 1px solid ${t.border}; vertical-align: middle; }
    .con-table tbody tr:last-child td { border-bottom: none; }
    .con-table tbody tr:hover { background: ${t.rowHover}; }

    .con-badge { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.03em; }
    .con-badge.alta { background: ${t.dangerBg}; color: ${t.danger}; }
    .con-badge.media { background: ${t.warningBg}; color: ${t.warning}; }
    .con-badge.baixa { background: ${t.successBg}; color: ${t.success}; }

    .con-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
    .con-icon-btn { width: 36px; height: 36px; border-radius: 10px; border: 1px solid ${t.border}; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; color: ${t.textSecondary}; transition: all 0.2s; }
    .con-icon-btn:hover { background: ${t.bgHover}; color: ${t.textActive}; border-color: ${t.borderActive}; }
    .con-icon-btn.danger:hover { background: ${t.dangerBg}; color: ${t.danger}; }
    .con-icon-btn.formacao:hover { background: ${t.successBg}; color: ${t.success}; border-color: ${t.success}; }
    .con-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    .con-pagination { display: flex; align-items: center; justify-content: space-between; padding: 16px 22px; border-top: 1px solid ${t.border}; color: ${t.textSecondary}; font-size: 0.84rem; }
    .con-pagination-controls { display: flex; align-items: center; gap: 4px; }
    .con-pagination-btn { width: 34px; height: 34px; border-radius: 8px; border: 1px solid ${t.border}; background: ${t.bgCard}; display: flex; align-items: center; justify-content: center; cursor: pointer; color: ${t.textSecondary}; transition: all 0.2s; }
    .con-pagination-btn:hover:not(:disabled) { background: ${t.bgHover}; color: ${t.textActive}; }
    .con-pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .con-pagination-num { width: 34px; height: 34px; border-radius: 8px; border: none; background: transparent; color: ${t.textSecondary}; cursor: pointer; font-weight: 500; }
    .con-pagination-num.active { background: ${t.bgActive}; color: ${t.textActive}; font-weight: 700; }

    .con-overlay { position: fixed; inset: 0; background: ${t.overlay}; backdrop-filter: blur(6px); z-index: 800; display: flex; align-items: center; justify-content: center; padding: 20px; }
    .con-modal { background: ${t.bgDropdown}; border: 1px solid ${t.border}; border-radius: 22px; box-shadow: ${t.shadow}; width: 100%; max-width: 420px; }
    .con-modal-header { padding: 24px 28px 20px; border-bottom: 1px solid ${t.border}; display: flex; align-items: center; justify-content: space-between; }
    .con-modal-title { font-size: 1.15rem; font-weight: 700; color: ${t.textPrimary}; }
    .con-modal-close { width: 32px; height: 32px; border-radius: 10px; border: none; background: ${t.bgHover}; color: ${t.textSecondary}; display: flex; align-items: center; justify-content: center; cursor: pointer; }
    .con-modal-close:hover { background: ${t.bgActive}; color: ${t.textActive}; }
    .con-delete-body { padding: 26px 28px; font-size: 0.9rem; color: ${t.textSecondary}; line-height: 1.6; }
    .con-modal-footer { padding: 20px 28px 24px; display: flex; gap: 14px; justify-content: flex-end; border-top: 1px solid ${t.border}; }
    .con-btn-secondary { height: 42px; padding: 0 22px; border-radius: 12px; border: 1px solid ${t.border}; background: transparent; color: ${t.textSecondary}; font-weight: 600; font-size: 0.84rem; cursor: pointer; }
    .con-btn-secondary:hover { background: ${t.bgHover}; color: ${t.textPrimary}; }
    .con-btn-danger { height: 42px; padding: 0 24px; border-radius: 12px; border: none; background: ${t.danger}; color: #fff; font-weight: 600; font-size: 0.84rem; cursor: pointer; }
    .con-btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

    .con-toast { position: fixed; bottom: 28px; right: 28px; z-index: 900; display: flex; align-items: center; gap: 12px; padding: 16px 22px; border-radius: 16px; background: ${t.bgDropdown}; border: 1px solid ${t.border}; box-shadow: ${t.shadow}; font-size: 0.86rem; color: ${t.textPrimary}; }
    .con-toast-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }

    @media (max-width: 640px) {
      .con-table thead { display: none; }
      .con-table tbody tr { display: flex; flex-direction: column; padding: 16px; }
      .con-table tbody td { padding: 0; border-bottom: none; }
      .con-actions { justify-content: flex-start; margin-top: 6px; }
      .con-search-box { width: 100%; }
      .con-pagination { flex-direction: column; gap: 10px; }
      .con-header-right { flex-direction: column; align-items: flex-start; gap: 10px; }
    }
  `;

  if (view !== "list") {
    return (
      <ConteudoFormScreen
        mode={view}
        conteudo={editingConteudo}
        t={t}
        dark={dark}
        onClose={closeForm}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <div className="con-page">
      <style>{styles}</style>
      <div className="con-container">
        <div className="con-header">
          <div>
            <h1 className="con-heading">Conteúdos</h1>
            <p className="con-sub">Gerir conteúdos para formação</p>
          </div>
          <div className="con-header-right">
            {!loadingStats && (
              <div className="con-limit-widget">
                <span>Formações:</span>
                <span className={`con-limit-number ${formStats.total_restante === 0 ? 'warning' : ''}`}>
                  {formStats.total_criado}/{formStats.total_permitido}
                </span>
                <div className="con-limit-bar">
                  <div
                    className={`con-limit-fill ${formStats.total_restante === 0 ? 'warning' : ''}`}
                    style={{ width: `${(formStats.total_criado / formStats.total_permitido) * 100}%` }}
                  />
                </div>
                <span style={{ fontSize: '0.78rem' }}>
                  {formStats.total_restante > 0 ? `${formStats.total_restante} restantes` : 'Esgotado'}
                </span>
              </div>
            )}
            <button className="con-btn-add" onClick={openAdd}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Novo conteúdo
            </button>
          </div>
        </div>

        {errorMsg && <div className="con-error-banner" style={{background: t.dangerBg, color: t.danger, padding: "14px 20px", borderRadius: 14, marginBottom: 24, display: "flex", gap: 10}}>⚠️ {errorMsg}</div>}

        <div className="con-toolbar">
          <div className="con-search-box">
            <span style={{color: t.textMuted}}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
            <input placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div className="con-table-card">
          {loading ? (
            <div>{Array.from({length:5}).map((_,i)=><div key={i} style={{padding:"14px 22px", display:"flex", gap:12}}><div style={{width:200, height:12, borderRadius:6, background:t.bgHover}}/><div style={{flex:1, height:12, borderRadius:6, background:t.bgHover}}/></div>)}</div>
          ) : filtered.length === 0 ? (
            <div style={{padding:"60px", textAlign:"center", color:t.textMuted}}>Nenhum conteúdo encontrado</div>
          ) : (
            <>
              <table className="con-table">
                <thead><tr><th>Título</th><th>Dificuldade</th><th style={{textAlign:"right"}}>Ações</th></tr></thead>
                <tbody>
                  {paginated.map(c => (
                    <tr key={c.id}>
                      <td><span style={{fontWeight:600}}>{c.titulo || "—"}</span></td>
                      <td><span className={`con-badge ${c.dificuldade?.toUpperCase() === "ALTA" ? "alta" : c.dificuldade?.toUpperCase() === "MÉDIA" || c.dificuldade?.toUpperCase() === "MÉDIA" ? "media" : "baixa"}`}>{c.dificuldade}</span></td>
                      <td>
                        <div className="con-actions">
                          <button
                            className="con-icon-btn formacao"
                            title={formStats.total_restante === 0 ? "Limite de formações atingido" : "Gerar formação"}
                            disabled={gerandoFormacaoId === c.id || formStats.total_restante === 0}
                            onClick={() => handleGerarFormacao(c)}
                          >
                            {gerandoFormacaoId === c.id ? <span className="mini-spinner" style={{borderColor:`${t.border}`, borderTopColor:t.textActive}}/> : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2 3 14h7l-1 8 10-12h-7z"/></svg>}
                          </button>
                          <button className="con-icon-btn" title="Editar" onClick={() => openEdit(c)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg></button>
                          <button className="con-icon-btn danger" title="Eliminar" onClick={() => setDeletingConteudo(c)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="con-pagination">
                <span>{paginated.length} de {filtered.length}</span>
                <div className="con-pagination-controls">
                  <button className="con-pagination-btn" disabled={safePage<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg></button>
                  {Array.from({length:totalPages},(_,i)=>i+1).map(num=><button key={num} className={`con-pagination-num ${num===safePage?"active":""}`} onClick={()=>setPage(num)}>{num}</button>)}
                  <button className="con-pagination-btn" disabled={safePage>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {deletingConteudo && (
        <div className="con-overlay" onMouseDown={e=>e.target===e.currentTarget&&setDeletingConteudo(null)}>
          <div className="con-modal">
            <div className="con-modal-header"><div className="con-modal-title">Eliminar conteúdo</div><button className="con-modal-close" onClick={()=>setDeletingConteudo(null)}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button></div>
            <div className="con-delete-body">Eliminar <strong>{deletingConteudo.titulo || "este conteúdo"}</strong>? Esta ação é irreversível.</div>
            <div className="con-modal-footer"><button className="con-btn-secondary" onClick={()=>setDeletingConteudo(null)}>Cancelar</button><button className="con-btn-danger" onClick={handleDelete} disabled={actionLoading}>{actionLoading?"Eliminando...":"Eliminar"}</button></div>
          </div>
        </div>
      )}

      {notification && (
        <div className="con-toast"><span className="con-toast-dot" style={{background:notification.type==="success"?t.success:t.danger}}/>{notification.text}</div>
      )}
    </div>
  );
}

// ── O componente ConteudoFormScreen permanece o mesmo que já estava, não sofreu alterações ──
/* ════════════════════════ FORMULÁRIO (ADICIONAR / EDITAR) ════════════════════════ */
 // Substitua o componente ConteudoFormScreen pelo código abaixo.
function ConteudoFormScreen({ mode, conteudo, t, dark, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  const [titulo, setTitulo] = useState(isEdit ? conteudo?.titulo ?? "" : "");
  const [files, setFiles] = useState([]);
  const [transcribing, setTranscribing] = useState(false);
  const [texto, setTexto] = useState(isEdit ? conteudo?.texto ?? "" : "");
  const [dificuldade, setDificuldade] = useState(isEdit ? conteudo?.dificuldade ?? "" : "");

  // Novos campos
  const [duracaoQuiz, setDuracaoQuiz] = useState(
    isEdit ? (conteudo?.duracao_quiz || "01:00:00") : "01:00:00"
  );
  const [totalPergunta, setTotalPergunta] = useState(
    isEdit ? (conteudo?.total_pergunta != null ? String(conteudo.total_pergunta) : "10") : "10"
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerText !== texto) {
      editorRef.current.innerText = texto;
    }
  }, [texto]);

  const handleFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length) setFiles(prev => [...prev, ...selected]);
    e.target.value = "";
  };

  const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

  const handleTranscrever = async () => {
    if (!files.length) { setError("Selecione pelo menos um documento."); return; }
    setError("");
    setTranscribing(true);
    try {
      const result = await Api.extract(files);
      if (result?.success === false) {
        setError(result.message || "Erro ao transcrever.");
      } else {
        const novo = result.text || "";
        setTexto(prev => prev ? `${prev}\n\n${novo}` : novo);
        setFiles([]);
      }
    } catch {
      setError("Erro ao transcrever documentos.");
    } finally {
      setTranscribing(false);
    }
  };

  const getEditorText = () => editorRef.current ? editorRef.current.innerText : "";

  // Função para formatar duração em HH:MM:SS
  const formatDuracao = (valor) => {
    const digits = valor.replace(/\D/g, "").slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`;
  };

  const handleDuracaoChange = (e) => {
    setDuracaoQuiz(formatDuracao(e.target.value));
  };

  const handleTotalPerguntaChange = (e) => {
    const apenasDigitos = e.target.value.replace(/\D/g, "");
    setTotalPergunta(apenasDigitos);
  };

  const validate = () => {
    if (!titulo.trim()) { setError("O título é obrigatório."); return false; }
    const editorText = getEditorText().trim();
    if (!editorText) { setError("O texto do conteúdo é obrigatório."); return false; }
    if (!dificuldade) { setError("Selecione a dificuldade."); return false; }

    // Validação da duração do quiz
    if (!/^\d{2}:\d{2}:\d{2}$/.test(duracaoQuiz)) {
      setError("A duração do quiz deve estar no formato 00:00:00.");
      return false;
    }

    // Validação do total de perguntas
    const total = parseInt(totalPergunta, 10);
    if (isNaN(total) || total <= 0) {
      setError("Total de perguntas deve ser um número inteiro positivo.");
      return false;
    }

    setError("");
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const textoFinal = getEditorText().trim();
    setSaving(true);
    try {
      const payload = {
        titulo: titulo.trim(),
        texto: textoFinal,
        dificuldade,
        duracao_quiz: duracaoQuiz,
        total_pergunta: parseInt(totalPergunta, 10),
      };

      let response;
      if (isEdit) {
        response = await Api.updateConteudo({ id: conteudo.id, ...payload });
      } else {
        const storedUser = localStorage.getItem('user');
        const user = storedUser ? JSON.parse(storedUser) : null;
        response = await Api.addConteudo({ ...payload, id: user?.id });
      }

      if (response?.success === false) {
        setError(response.message || "Erro ao guardar.");
      } else {
        onSaved(isEdit ? "Conteúdo atualizado." : "Conteúdo criado.");
      }
    } catch (err) {
      setError(err?.message || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  const execCommand = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  const styles = `
    .cfs-page { font-family: 'Inter', system-ui, sans-serif; background: ${t.bg}; min-height: 100vh; padding: 32px 20px; }
    .cfs-container { max-width: 1200px; margin: 0 auto; }
    .cfs-header { display: flex; align-items: center; gap: 16px; margin-bottom: 28px; }
    .cfs-back { width: 40px; height: 40px; border-radius: 12px; border: 1px solid ${t.border}; background: ${t.bgCard}; display: flex; align-items: center; justify-content: center; cursor: pointer; color: ${t.textSecondary}; transition: all 0.2s; }
    .cfs-back:hover { background: ${t.bgHover}; color: ${t.textActive}; }
    .cfs-heading { font-size: 1.5rem; font-weight: 700; color: ${t.textPrimary}; }
    .cfs-sub { font-size: 0.86rem; color: ${t.textMuted}; }

    .cfs-card { background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 18px; padding: 24px; margin-bottom: 20px; }
    .cfs-card-title { font-size: 0.95rem; font-weight: 700; color: ${t.textPrimary}; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }

    /* Linha combinada */
    .cfs-info-row { display: flex; gap: 20px; align-items: flex-start; flex-wrap: wrap; }
    .cfs-field { display: flex; flex-direction: column; gap: 6px; }
    .cfs-field label { font-size: 0.78rem; font-weight: 600; color: ${t.textSecondary}; white-space: nowrap; }
    .cfs-input, .cfs-select {
      height: 44px; border-radius: 12px; border: 1px solid ${t.border};
      background: ${t.bgInput}; color: ${t.textPrimary}; padding: 0 16px;
      font-size: 0.86rem; outline: none; width: 100%; box-sizing: border-box;
    }
    .cfs-input:focus, .cfs-select:focus { border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.15); }

    /* Colunas com larguras mínimas para evitar sobreposição */
    .cfs-col-titulo { flex: 1 1 200px; min-width: 150px; }
    .cfs-col-dificuldade { flex: 0 0 160px; }
    .cfs-col-upload { flex: 1 1 300px; min-width: 250px; }
    .cfs-col-duracao { flex: 0 0 190px; min-width: 160px; }
    .cfs-col-total { flex: 0 0 160px; min-width: 140px; }

    .cfs-upload-buttons { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .cfs-btn-select-file {
      height: 44px; padding: 0 16px; border-radius: 12px; border: 1px solid ${t.border};
      background: ${t.bgInput}; color: ${t.textPrimary}; font-size: 0.84rem;
      font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
      transition: 0.2s; white-space: nowrap;
    }
    .cfs-btn-select-file:hover { background: ${t.bgHover}; border-color: ${t.borderActive}; }

    .cfs-btn-transcrever {
      height: 44px; padding: 0 18px; border-radius: 12px; border: none;
      background: linear-gradient(135deg, #00aaff, #0044ee); color: #fff;
      font-weight: 600; font-size: 0.82rem; cursor: pointer;
      display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
    }
    .cfs-btn-transcrever:disabled { opacity: 0.6; cursor: not-allowed; }

    .cfs-file-list { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
    .cfs-file-item { display: flex; align-items: center; gap: 8px; padding: 6px 10px; border-radius: 8px; background: ${t.bgInput}; border: 1px solid ${t.border}; }
    .cfs-file-remove { background: none; border: none; color: ${t.textMuted}; cursor: pointer; }
    .cfs-file-remove:hover { color: ${t.danger}; }

    /* Editor */
    .cfs-editor-toolbar { display: flex; align-items: center; gap: 4px; padding: 8px 12px; background: ${dark?"#1e293b":"#f8fafc"}; border: 1px solid ${t.border}; border-bottom: none; border-radius: 14px 14px 0 0; flex-wrap: wrap; }
    .cfs-tool-btn { width: 34px; height: 34px; border-radius: 8px; border: none; background: transparent; color: ${t.textSecondary}; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 0.85rem; font-weight: 600; transition: 0.2s; }
    .cfs-tool-btn:hover { background: ${t.bgActive}; color: ${t.textActive}; }
    .cfs-tool-divider { width: 1px; height: 20px; background: ${t.border}; margin: 0 4px; }
    .cfs-editor-area {
      height: 600px; overflow-y: auto; padding: 18px;
      border: 1px solid ${t.border}; border-top: none;
      border-radius: 0 0 14px 14px; background: ${t.bgInput};
      color: ${t.textPrimary}; font-size: 0.92rem; line-height: 1.65; outline: none;
    }
    .cfs-editor-area:focus { border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.15); }

    .cfs-footer { display: flex; gap: 14px; justify-content: flex-end; margin-top: 8px; }
    .cfs-btn-secondary { height: 44px; padding: 0 24px; border-radius: 12px; border: 1px solid ${t.border}; background: transparent; color: ${t.textSecondary}; font-weight: 600; cursor: pointer; }
    .cfs-btn-primary { height: 44px; padding: 0 26px; border-radius: 12px; border: none; background: linear-gradient(135deg, #00aaff, #0044ee); color: #fff; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px; }
    .cfs-btn-primary:disabled { opacity: 0.6; }
    .mini-spinner { width: 16px; height: 16px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; animation: spin 0.7s linear infinite; display: inline-block; }
    @keyframes spin { to { transform: rotate(360deg); } }

    .cfs-error-banner { background: ${t.dangerBg}; color: ${t.danger}; padding: 14px 20px; border-radius: 14px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

    @media (max-width: 800px) {
      .cfs-info-row { flex-direction: column; }
    }
  `;

  return (
    <div className="cfs-page">
      <style>{styles}</style>
      <div className="cfs-container">
        <div className="cfs-header">
          <button className="cfs-back" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <div className="cfs-heading">{isEdit ? "Editar conteúdo" : "Novo conteúdo"}</div>
            <div className="cfs-sub">{isEdit ? "Altere título, dificuldade e texto." : "Preencha os dados e faça upload de documentos para transcrição."}</div>
          </div>
        </div>

        {error && <div className="cfs-error-banner">⚠️ {error}</div>}

        {/* Cartão com Título, Dificuldade e Upload na mesma linha */}
        <div className="cfs-card">
          <div className="cfs-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Informações e Documentos
          </div>
          <div className="cfs-info-row">
            <div className="cfs-field cfs-col-titulo">
              <label>Título</label>
              <input className="cfs-input" placeholder="Ex: Fundamentos de Gestão" value={titulo} onChange={e => setTitulo(e.target.value)} />
            </div>

            <div className="cfs-field cfs-col-dificuldade">
              <label>Dificuldade</label>
              <select className="cfs-select" value={dificuldade} onChange={e => setDificuldade(e.target.value)}>
                <option value="" disabled>Selecionar...</option>
                {DIFICULDADES.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div className="cfs-field cfs-col-upload">
              <label>Documentos (transcrição)</label>
              <div className="cfs-upload-buttons">
                <button type="button" className="cfs-btn-select-file" onClick={() => fileInputRef.current?.click()}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  Selecionar ficheiro
                </button>
                <input ref={fileInputRef} type="file" multiple accept=".doc,.docx,.pdf,.txt" style={{display:"none"}} onChange={handleFilesSelected} />
                {files.length > 0 && (
                  <button className="cfs-btn-transcrever" onClick={handleTranscrever} disabled={transcribing}>
                    {transcribing ? <span className="mini-spinner"/> : "Transcrever"}
                  </button>
                )}
              </div>

              {files.length > 0 && (
                <div className="cfs-file-list">
                  {files.map((f,i) => (
                    <div key={i} className="cfs-file-item">
                      <span style={{flex:1, fontSize:"0.82rem", color:t.textPrimary}}>{f.name}</span>
                      <button className="cfs-file-remove" onClick={()=>removeFile(i)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cartão com configuração do quiz */}
        <div className="cfs-card">
          <div className="cfs-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Configuração do Quiz
          </div>
          <div className="cfs-info-row">
            <div className="cfs-field cfs-col-duracao">
              <label>Duração do Quiz (HH:MM:SS)</label>
              <input
                className="cfs-input"
                value={duracaoQuiz}
                onChange={handleDuracaoChange}
                placeholder="01:00:00"
                maxLength={8}
              />
            </div>

            <div className="cfs-field cfs-col-total">
              <label>Total de Perguntas</label>
              <input
                className="cfs-input"
                type="text"
                inputMode="numeric"
                value={totalPergunta}
                onChange={handleTotalPerguntaChange}
                placeholder="10"
                maxLength={4}
              />
            </div>
          </div>
        </div>

        {/* Editor de texto */}
        <div className="cfs-card">
          <div className="cfs-card-title">Texto do conteúdo</div>
          <div className="cfs-editor-toolbar">
            <button className="cfs-tool-btn" onClick={() => execCommand("bold")} title="Negrito"><b>B</b></button>
            <button className="cfs-tool-btn" onClick={() => execCommand("italic")} title="Itálico"><i>I</i></button>
            <button className="cfs-tool-btn" onClick={() => execCommand("underline")} title="Sublinhado"><u>U</u></button>
            <span className="cfs-tool-divider" />
            <button className="cfs-tool-btn" onClick={() => execCommand("insertUnorderedList")} title="Lista">•</button>
            <button className="cfs-tool-btn" onClick={() => execCommand("insertOrderedList")} title="Lista numerada">1.</button>
          </div>
          <div
            ref={editorRef}
            className="cfs-editor-area"
            contentEditable
            suppressContentEditableWarning
            onInput={() => {}}
          />
          <div style={{fontSize:"0.7rem", color:t.textMuted, marginTop:6, textAlign:"right"}}>
            Pode formatar visualmente — o texto será guardado sem formatação.
          </div>
        </div>

        <div className="cfs-footer">
          <button className="cfs-btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="cfs-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="mini-spinner"/> : null}
            {isEdit ? "Guardar alterações" : "Criar conteúdo"}
          </button>
        </div>
      </div>
    </div>
  );
}